import React from "react";
import { NavLink } from "react-router-dom";
import { AppPath } from "utils/constants";
import { JA } from "utils/strings.ja";

// Shared look of the screens before sign-in completes: login, forgot password,
// first-login password change, OTP entry and OTP setup.
export const FIELD =
  "w-full py-3 pl-6 pr-10 bg-white border rounded-lg outline-hidden border-stroke focus:border-brand focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-brand";

// 6-digit codes (OTP, password reset): centred, spaced, digits only
export const CODE_FIELD =
  "w-full py-3 px-4 bg-white border rounded-lg outline-hidden border-stroke text-center text-lg tracking-[0.3em] placeholder:text-base placeholder:tracking-normal focus:border-brand dark:border-form-strokedark dark:bg-form-input dark:focus:border-brand";

export const PRIMARY_BUTTON =
  "mt-2 w-full p-3 font-medium text-white transition border rounded-lg cursor-pointer border-brand bg-brand hover:bg-brand-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

export const LABEL = "mb-2.5 block font-medium text-black dark:text-white";

/** Keeps the digits of a typed / pasted code, at most `max`. */
export const digitsOnly = (value, max = 6) => value.replace(/\D/g, "").slice(0, max);

/** A message for the user above the form (utils/loginNotice). */
export function Notice({ notice }) {
  if (!notice?.text) return null;
  const tone =
    notice.kind === "error"
      ? "border-red-200 bg-red-50 text-red-700"
      : "border-brand/20 bg-brand-soft text-black dark:text-white";
  return (
    <p
      role={notice.kind === "error" ? "alert" : "status"}
      className={`mb-4 whitespace-pre-line rounded-lg border px-4 py-2.5 text-sm [word-break:auto-phrase] ${tone}`}
    >
      {notice.text}
    </p>
  );
}

export function BackToLogin() {
  return (
    <div className="mt-4 text-center">
      <NavLink to={AppPath.Login} className="text-sm text-brand hover:underline">
        {JA.auth.backToLogin}
      </NavLink>
    </div>
  );
}

/**
 * The system name above a white card. `title` / `description` head the card;
 * the login screen leaves them out (its button already says ログイン).
 * Messages go in one place, the top of the card: `error` (the form's own) or
 * else `notice` (utils/loginNotice).
 */
export default function AuthCard({ title, description, error, notice, children }) {
  const message = error ? { text: error, kind: "error" } : notice;
  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-6">
      <div className="w-full max-w-[480px]">
        {/* which system this is: the console and the VMS both have a login screen */}
        <header className="mb-4 text-center">
          <h1 className="text-2xl font-bold tracking-wide text-brand">{JA.auth.appName}</h1>
          <p className="mt-1 text-sm text-body dark:text-bodydark">{JA.auth.appSubtitle}</p>
        </header>

        <div className="rounded-xl border border-stroke bg-white p-6 shadow-default sm:px-10 sm:py-8 dark:border-strokedark dark:bg-boxdark">
          {title && (
            <h2 className="text-xl font-bold text-black [word-break:auto-phrase] dark:text-white">{title}</h2>
          )}
          {description && (
            <p className="mt-2 text-sm leading-6 text-body [word-break:auto-phrase] dark:text-bodydark">{description}</p>
          )}
          <div className={title || description ? "mt-5" : ""}>
            <Notice notice={message} />
            {children}
          </div>
        </div>
      </div>
    </main>
  );
}
